import { memo, useMemo } from 'react';

import { FrameCanvas } from './FrameCanvas';
import { buildCrossSection, type RowRange } from './reslice';
import type { Point } from './hermite';
import type { ManifestV2, Volume } from './types';
import { containBoxStyle } from './viewerTheme';

const PANE_BG = '#000000';
const PANE_BORDER = '#cfd6dd';
const ACCENT = '#45c3d6';
const RED = '#f2564a';
const MONO = "ui-monospace,'SF Mono','Cascadia Code',monospace";

export const CrossSectionStrip = memo(function CrossSectionStrip({
    volume,
    manifest,
    pts,
    normals,
    probeCol,
    center,
    width,
    flipVertical = false,
    widthMm,
    gapMm,
    count = 5,
    rows
}: {
    volume: Volume;
    manifest: ManifestV2;
    pts: Point[];
    normals: Point[];
    probeCol: number;
    center: number;
    width: number;
    /** Reverses the superior/inferior (z) orientation — see `displayZ` in volume.ts. */
    flipVertical?: boolean;
    widthMm: number;
    gapMm: number;
    count?: number;
    /** Optional vertical (S-I) crop of the cuts; full volume depth when omitted. */
    rows?: RowRange;
}) {
    const sx = manifest.spacing_mm.x;
    const sz = manifest.spacing_mm.z;
    const L = pts.length;
    const halfW = widthMm / 2 / sx;

    const stepCols = Math.max(1, Math.round(gapMm / sx));
    const clampCol = (c: number) => Math.max(0, Math.min(L - 1, c));
    const span = Math.floor(count / 2);
    const offsets = Array.from({ length: count }, (_, i) => i - span);
    const cols = offsets.map((o) => clampCol(probeCol + o * stepCols));
    const labels = offsets.map((o) => (o === 0 ? 'n' : o < 0 ? `n${o}` : `n+${o}`));

    const cuts = useMemo(
        () =>
            cols.map((c) =>
                buildCrossSection(volume, pts, normals, c, halfW, false, rows, flipVertical)
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [volume, pts, normals, cols.join(','), halfW, rows?.start, rows?.count, flipVertical]
    );
    const tileAspect = cuts.length ? widthMm / (cuts[0].h * sz) : 1;

    return (
        <div className="flex h-full w-full items-stretch gap-1">
            {cuts.map((f, i) => {
                const isCenter = offsets[i] === 0;
                const accent = isCenter ? RED : ACCENT;
                return (
                    <div
                        key={offsets[i]}
                        className="relative flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden"
                        style={{
                            background: PANE_BG,
                            border: `1px solid ${isCenter ? RED : PANE_BORDER}`,
                            borderRadius: 7,
                            fontFamily: MONO,
                            containerType: 'size'
                        }}
                    >
                        <div className="max-h-full max-w-full" style={containBoxStyle(tileAspect)}>
                            <FrameCanvas
                                frame={f}
                                center={center}
                                width={width}
                                className="block h-full w-full"
                            />
                        </div>
                        <div
                            className="pointer-events-none absolute"
                            style={{
                                top: 9,
                                left: 11,
                                fontWeight: 600,
                                fontSize: 10.5,
                                letterSpacing: '0.1em',
                                color: accent
                            }}
                        >
                            {labels[i]}
                        </div>
                        <div
                            className="pointer-events-none absolute"
                            style={{ bottom: 8, right: 9, fontSize: 9.5, color: '#6a7883' }}
                        >
                            {isCenter ? 'probe' : `${(offsets[i] * gapMm).toFixed(1)}`}
                        </div>
                    </div>
                );
            })}
        </div>
    );
});
